<template>
  <section id="operationType">
    <b-modal
      v-model="modalVisible"
      :width="800"
      scroll="keep"
      :on-cancel="closeModal"
    >
      <div class="ot-modal-card">
        <div class="ot-modal-header">
          <h3 class="ot-modal-title">
            {{ modalTitle }}
          </h3>
          <button
            class="ot-modal-close"
            @click="closeModal"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div class="ot-modal-body">
          <div class="grid-columns">
            <div class="column">
              <b-field label="Tipo de vehículo">
                <b-select
                  placeholder="Selecciona el tipo de vehículo"
                  expanded
                  :disabled="isLoadingVehicles"
                  :loading="isLoadingVehicles"
                  name="vehicleType_form"
                  v-model="selectedVehicleType"
                >
                  <option
                    v-for="option in availableVehiclesForForm"
                    :value="option.id"
                    :key="option.id"
                    :id="option.id"
                  >
                    {{ option.title }}
                  </option>
                </b-select>
              </b-field>
            </div>
            <div class="column">
              <b-field label="Tipo de operación">
                <b-select
                  placeholder="Selecciona el tipo de operación"
                  expanded
                  :disabled="isOperationDisabledForm || isLoadingOperations"
                  :loading="isLoadingOperations"
                  name="operationType_form"
                  v-model="selectedOperationType"
                >
                  <option
                    v-for="option in operationTypes"
                    :value="option.id"
                    :key="option.id"
                    :id="option.id"
                  >
                    {{ option.title }}
                  </option>
                </b-select>
              </b-field>
            </div>
          </div>
        </div>

        <div class="ot-modal-footer">
          <vs-button
            :color="submitBtnColor"
            type="filled"
            :icon="submitBtnIcon"
            class="ot-action-btn mr-2"
            @click="addOrUpdatePair"
            :disabled="!canSubmit"
          >
            {{ actionLabel }}
          </vs-button>
          <vs-button
            color="dark"
            type="flat"
            icon="close"
            class="ot-action-btn"
            @click="closeModal"
          >
            Cancelar
          </vs-button>
        </div>
      </div>
    </b-modal>

    <div class="operation-type-card">
      <div class="card-header-operation-type">
        <div class="title">
          {{ headerTitle }}
        </div>
      </div>

      <div class="card-body">
        <p class="w-full description-text">
          Agregue relaciones entre tipos de vehículos y operaciones permitidas.
        </p>
        <div class="summary">
          <div class="summary-title">Relaciones</div>
          <div class="chips">
            <span
              class="chip chip-editable"
              v-for="(pair, i) in pairs"
              :key="i"
              @click="onChipClick(i)"
              :title="'Editar relación'"
            >
              {{ getVehicleTitle(pair.vehicleType) }} →
              {{ getOperationTitle(pair.operationType) }}
              <span class="chip-close" @click.stop="removePair(i)">×</span>
            </span>
            <span
              class="chip chip-add"
              @click="openCreateModal"
              title="Agregar relación"
              >+</span
            >
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ROUTES } from '@/global/api_routes'

export default {
  name: 'OperationType',
  props: {
    field: {
      type: Object,
      default: () => ({}),
    },
    value: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    vehicleTypes: [],
    operationTypes: [],
    isLoadingVehicles: false,
    isLoadingOperations: false,
    pairs: [],
    selectedVehicleType: null,
    selectedOperationType: null,
    editingIndex: null,
    modalVisible: false,
    syncingFromParent: false,
  }),
  mounted() {
    this.getVehicleTypes()
    this.getOperationTypes()
    this.setPairsFromValue(this.value)
  },
  watch: {
    value(val) {
      this.setPairsFromValue(val)
    },
  },
  computed: {
    usedVehicleIds() {
      const used = this.pairs.map((p) => p.vehicleType).filter((code) => !!code)
      return used
    },
    availableVehiclesForForm() {
      const list = Array.isArray(this.vehicleTypes) ? this.vehicleTypes : []
      const editing = this.isEditing()
      if (!editing) {
        const filtered = list.filter(
          (v) => !this.usedVehicleIds.includes(v.id)
        )
        return filtered
      }
      const editPair = this.pairs[this.editingIndex]
      let editingVehicle = null
      if (editPair && editPair.vehicleType)
        editingVehicle = editPair.vehicleType
      const filtered = list.filter(
        (v) =>
          !this.usedVehicleIds.includes(v.id) || v.id === editingVehicle
      )
      return filtered
    },
    canSubmit() {
      const validVehicle = !!this.selectedVehicleType
      if (!validVehicle) return false
      const validOperation = !!this.selectedOperationType
      if (!validOperation) return false
      return true
    },
    actionLabel() {
      const editing = this.isEditing()
      if (editing) return 'Actualizar relación'
      return 'Agregar relación'
    },
    isOperationDisabledForm() {
      return !this.selectedVehicleType
    },
    modalTitle() {
      const editing = this.isEditing()
      if (editing) return 'Editar relación'
      return 'Agregar relación'
    },
    submitBtnColor() {
      const editing = this.isEditing()
      if (editing) return 'warning'
      return 'success'
    },
    submitBtnIcon() {
      const editing = this.isEditing()
      if (editing) return 'edit'
      return 'add'
    },
    headerTitle() {
      const label = 'Tipo operación por vehículo'
      const t = this.field && this.field.title
      if (t) return `${label} — ${t}`
      return label
    },
  },
  methods: {
    emitPairs() {
      const result = this.pairs
        .filter((p) => !!p.vehicleType && !!p.operationType)
        .map((p) => ({
          vehicleType: p.vehicleType,
          operationType: p.operationType,
        }))
      this.$emit('operation-types-change', result)
      this.$emit('input', result)
    },
    setPairsFromValue(val) {
      const list = Array.isArray(val) ? val : []
      this.syncingFromParent = true
      this.pairs = list
        .filter((p) => p && p.vehicleType && p.operationType)
        .map((p) => ({
          vehicleType: p.vehicleType,
          operationType: p.operationType,
        }))
      this.$nextTick(() => {
        this.syncingFromParent = false
      })
    },
    getVehicleTypes() {
      this.isLoadingVehicles = true
      this.$http
        .get(ROUTES.DASHBOARD.ENTITY.GET_LIST_BY_KIND, {
          params: { kindId: 5 },
        })
        .then(({ data }) => {
          const valid = data && data.code > 0 && Array.isArray(data.info)
          if (!valid) {
            this.vehicleTypes = []
            return
          }
          this.vehicleTypes = data.info
        })
        .finally(() => {
          this.isLoadingVehicles = false
        })
    },
    getOperationTypes() {
      this.isLoadingOperations = true
      this.$http
        .get(ROUTES.DASHBOARD.ENTITY.GET_LIST_BY_KIND, {
          params: { kindId: 42 },
        })
        .then(({ data }) => {
          const valid = data && data.code > 0 && Array.isArray(data.info)
          if (!valid) {
            this.operationTypes = []
            return
          }
          this.operationTypes = data.info
        })
        .finally(() => {
          this.isLoadingOperations = false
        })
    },
    getVehicleTitle(id) {
      const v = this.vehicleTypes.find((it) => it.id === id)
      if (!v) return '—'
      return v.title
    },
    getOperationTitle(id) {
      const o = this.operationTypes.find((it) => it.id === id)
      if (!o) return '—'
      return o.title
    },
    clearForm() {
      this.selectedVehicleType = null
      this.selectedOperationType = null
    },
    addOrUpdatePair() {
      if (!this.canSubmit) return
      const pair = this.pairFromSelection()
      const isEditing = this.isEditing()
      if (isEditing) {
        this.updatePairAt(this.editingIndex, pair)
        return
      }
      const existIdx = this.findExistingIndexByVehicle(pair.vehicleType)
      const exists = existIdx > -1
      if (exists) {
        this.updatePairAt(existIdx, pair)
        return
      }
      this.createPair(pair)
    },
    pairFromSelection() {
      return {
        vehicleType: this.selectedVehicleType,
        operationType: this.selectedOperationType,
      }
    },
    isEditing() {
      return this.editingIndex !== null
    },
    findExistingIndexByVehicle(vehicleType) {
      return this.pairs.findIndex((p) => p.vehicleType === vehicleType)
    },
    updatePairAt(index, pair) {
      this.$set(this.pairs, index, pair)
      this.removeDuplicateByVehicle(pair.vehicleType, index)
      this.finalizeSubmit()
    },
    createPair(pair) {
      this.pairs.push(pair)
      this.finalizeSubmit()
    },
    removeDuplicateByVehicle(vehicleType, skipIndex) {
      const otherIdx = this.pairs.findIndex(
        (p, i) => p.vehicleType === vehicleType && i !== skipIndex
      )
      const hasDuplicate = otherIdx > -1
      if (!hasDuplicate) return
      this.pairs.splice(otherIdx, 1)
    },
    finalizeSubmit() {
      this.clearForm()
      this.emitPairs()
      this.modalVisible = false
      this.editingIndex = null
    },
    onChipClick(index) {
      const isValid =
        typeof index === 'number' && index > -1 && index < this.pairs.length
      if (!isValid) return
      const pair = this.pairs[index]
      this.editingIndex = index
      this.selectedVehicleType = pair.vehicleType
      this.selectedOperationType = pair.operationType
      this.modalVisible = true
    },
    openCreateModal() {
      this.editingIndex = null
      this.clearForm()
      this.modalVisible = true
    },
    closeModal() {
      this.modalVisible = false
      this.editingIndex = null
      this.clearForm()
    },
    cancelEdit() {
      this.editingIndex = null
      this.clearForm()
    },
    removePair(index) {
      const isValid =
        typeof index === 'number' && index > -1 && index < this.pairs.length
      if (!isValid) return
      this.pairs.splice(index, 1)
      const isEditingRemoved = this.editingIndex === index
      if (isEditingRemoved) {
        this.editingIndex = null
        this.clearForm()
      }
      this.emitPairs()
    },
  },
}
</script>

<style>
#operationType {
  padding: 0;
}
.operation-type-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}
.ot-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #edf2f7;
}
.ot-modal-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #2c3e50;
}
.ot-modal-close {
  background: transparent;
  border: none;
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  color: #6b7c93;
}
.ot-modal-close:hover {
  color: #2c3e50;
}
.ot-modal-body {
  padding: 16px 20px 8px;
}
.ot-modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px 16px;
  border-top: 1px solid #edf2f7;
}
.ot-action-btn {
  border-radius: 8px;
  padding: 10px 16px;
  font-weight: 600;
  width: auto; /* no forced width */
}
.card-header-operation-type {
  background-color: red;
  background: linear-gradient(90deg, #f7fafc 0%, #ffffff 100%);
  border-bottom: 1px solid #edf2f7;
  padding: 16px 20px;
}
.card-header-operation-type .title {
  font-weight: 700;
  font-size: 1.1rem;
  color: #2c3e50;
}
.card-header-operation-type .subtitle {
  font-size: 0.9rem;
  color: #6b7c93;
  margin-top: 4px;
}
.card-body {
  padding: 20px;
}
.form-row {
  align-items: center;
  background: #fbfdff;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 12px;
}
.column-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}
.summary {
  margin-top: 16px;
  border-top: 1px dashed #e6eaf0;
  padding-top: 12px;
}
.summary-title {
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 8px;
  color: #34495e;
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.chip {
  position: relative;
  background: #eef5ff;
  color: #2b6cb0;
  border: 1px solid #cfe0ff;
  border-radius: 999px;
  padding: 6px 28px 6px 12px;
  font-size: 0.85rem;
}
.chip-editable {
  cursor: text;
}
.chip-editable:hover {
  background: #e9f1ff;
  border-color: #bfd6ff;
  text-decoration: underline;
}
.chip-close {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #2b6cb0;
  font-weight: bold;
}
.chip-close:hover {
  color: #1a365d;
}
.w-full {
  width: 100%;
}
.chip-add {
  background: #2b6cb0;
  color: #ffffff;
  border: 1px solid #2b6cb0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: auto;
  min-width: 32px;
  padding: 6px 12px;
  font-weight: bold;
}
.description-text {
  color: #6b7c93;
  margin-bottom: 0 !important;
}
</style>
